import React, { useEffect } from 'react';
import Select from 'react-select';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCode } from 'react-qr-code';
import { authService } from '../services/authService';
import StatusBadge from '../components/StatusBadge';
import ActionsMenu from '../components/ActionsMenu';
import EventDetailMember from '../components/EventDetailMember';
import EventsBottomNav from '../components/EventsBottomNav';
import { DSAlert, DSBackLink, DSBadge, DSButton, DSCard, DSCheckbox, DSIcon, DSInput, DSModal, DSSelect, DSTextarea } from '../design-system/primitives';
import { getCategoryLabel, getCategoryVariant, isEventCancelled } from '../utils/eventHelpers';
import EventCancelledBanner from '../components/EventCancelledBanner';
import { formatVsPar, describeNet, formatHandicap, previewWeeklyPayouts, ordinalPlace, UNPLAYED_HOLE_RESULT } from '../utils/pingolfScoring';
import { formatScoreAbbreviated } from '../utils/formatScore';
import { pingolfBackTarget } from '../utils/pingolfNav';
import { canPurchasePingolfCards as canPurchasePingolfCardsFor, getPingolfSalesState } from '../utils/pingolfLifecycle';
import PinGolfStats from '../components/PinGolfStats';
import { useEventDetails } from '../hooks/useEventDetails';
import PinGolfScore, { PinGolfScoreLegend } from '../components/PinGolfScore';
import EventPhotoControl from '../components/EventPhotoControl';
import TournamentDetailsFields, { isTournament } from '../components/TournamentDetailsFields';
import pingolfHero from '../assets/pingolf-hero.webp';
import './pingolfHeroShared.css';
import './pingolfShared.css';
import './EventDetails.css';
import { personName } from '../utils/personName';

const EVENT_PAYMENT_LINKS = [
  {
    id: 'video_board_pass',
    title: 'Video/Board Game Only Pass',
    url: 'https://square.link/u/jx13yFLd'
  },
  {
    id: 'upgrade_pass',
    title: 'Upgrade Pass',
    url: 'https://square.link/u/DzBCxdMF'
  }
];

const EVENT_DESCRIPTION_PREVIEW_LENGTH = 120;

function EventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  // A member has no Events tab, so "Back to Events" would strand them on a page outside
  // their own navigation. They came in from the League screen's Next Event card; send them
  // back there.
  const pingolfBack = pingolfBackTarget(currentUser);
  // Whether the leaderboard's scoring-help modal is open. The score legend used to sit under
  // the table permanently; it's the same explainer, now behind the header's help icon.
  const [showPingolfScoringHelp, setShowPingolfScoringHelp] = React.useState(false);
  // Whether the "why can't I buy a card?" explainer is open. The reason used to sit on the page
  // as a badge plus a sentence, which cost a lot of vertical space on a phone to answer a
  // question most players are not asking. It is now behind the dimmed Buy Card button.
  const [showPingolfPurchaseBlockedInfo, setShowPingolfPurchaseBlockedInfo] = React.useState(false);
  // "My Cards" help, behind the same header icon pattern the leaderboard uses.
  const [showPingolfCardsHelp, setShowPingolfCardsHelp] = React.useState(false);

  const {
    event, loading, isHost, error,
    isEditingEvent,
    editEventData, setEditEventData,
    showMyRsvpForm, setShowMyRsvpForm,
    myRsvpData, setMyRsvpData,
    myCurrentRsvp,
    eventUpdates,
    showFeaturedGameForm,
    featuredGameData, setFeaturedGameData,
    expandedHostBios,
    expandedDescription, setExpandedDescription,
    activePaymentQr,
    pingolfCards,
    loadingPingolfCards,
    buyingPingolfCard,
    purchaseTargetUserId, setPurchaseTargetUserId,
    pingolfPurchaseCandidates,
    pingolfPurchaseUserOptions,
    selectedPingolfPurchaseUserOption,
    useTabForPingolfPurchase, setUseTabForPingolfPurchase,
    currentUserTabEnabled,
    pingolfPurchaseError, setPingolfPurchaseError,
    pingolfLeaderboard,
    loadingPingolfLeaderboard,
    pingolfLeaderboardError,
    pingolfLeaderboardDetailRow, setPingolfLeaderboardDetailRow,
    showPingolfPurchaseModal, setShowPingolfPurchaseModal,
    confirmDialog, setConfirmDialog,
    confirmDialogSubmitting,
    handlePurchasePingolfCard,
    copyPaymentLink,
    showPaymentQr,
    closePaymentQr,
    toggleHostBio,
    truncateText,
    formatDate,
    formatShortDate,
    formatTimeRange,
    handleJumpToPingolfHostControls,
    handleEditEvent,
    handleCancelEditEvent,
    handleAssignGame,
    handleCancelFeaturedGame,
    handleSaveFeaturedGame,
    handleRemoveFeaturedGame,
    handleSaveEvent,
    handleEventPhotoChange,
    handleDeleteEvent,
    handleMyRsvp,
    saveMyRsvp,
    handleRemoveMyRsvp
  } = useEventDetails(eventId, currentUser, isAdmin, navigate);

  // The hallway sign notice lives in event_metadata, which the edit form already saves whole.
  // Choosing "None" removes the key rather than storing an empty notice.
  const hallwayNotice = editEventData.event_metadata?.hallway_notice || null;
  // A private event's line on the hallway sign, in place of its title. Empty shows only the time.
  const hallwayLabel = editEventData.event_metadata?.hallway_label || '';
  const setHallwayLabel = (label) => {
    const { hallway_label: _previous, ...rest } = editEventData.event_metadata || {};
    setEditEventData({
      ...editEventData,
      event_metadata: label ? { ...rest, hallway_label: label } : rest
    });
  };
  // Tournament details live in event_metadata too, keyed by each field's name.
  const handleTournamentFieldChange = (e) => {
    const { name, value } = e.target;
    setEditEventData({
      ...editEventData,
      event_metadata: { ...(editEventData.event_metadata || {}), [name]: value }
    });
  };
  const setHallwayNotice = (notice) => {
    const { hallway_notice: _previous, ...rest } = editEventData.event_metadata || {};
    setEditEventData({
      ...editEventData,
      event_metadata: notice ? { ...rest, hallway_notice: notice } : rest
    });
  };

  // EventManage's "Edit event" lives on another route, so it hands the intent over in the
  // URL. Nothing read it, which made that menu item look dead: it navigated here and the
  // page rendered as normal. Consume it once and strip it, so a refresh or a back-nav does
  // not reopen the form. Kept in the page rather than useEventDetails on purpose - the hook
  // takes `navigate` as an argument specifically so it stays free of router context.
  useEffect(() => {
    if (loading || !event) return;
    if (searchParams.get('edit') !== '1') return;
    if (isAdmin || isHost) handleEditEvent();
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // handleEditEvent is rebuilt every render; depending on it would re-run this effect
    // rather than guard it. The param delete above is what makes this fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, event, isAdmin, isHost, searchParams, setSearchParams]);

  if (loading) return <div className="loading">Loading event...</div>;
  if (!event) return <div className="error">Event not found</div>;

  const isPingolfNight = event.event_metadata?.pinball_format === 'pingolf';

  const isGameEvent = event.event_category === 'board_game' || event.event_category === 'video_game';
  const isFeaturedItemEvent = isGameEvent || event.event_category === 'book_club';
  const featuredItemLabel = event.event_category === 'book_club' ? 'Book' : 'Game';
  const canManageFeaturedGame = isAdmin || isHost;
  // The member screen renders this, so it has to be settled before the early return below.
  // A null tells the screen this category has nothing featured; an object with an empty name
  // is a night whose game has not been picked yet, which is worth saying out loud.
  const featuredItem = isFeaturedItemEvent ? {
    label: featuredItemLabel,
    icon: event.event_category === 'book_club' ? 'book' : 'gamepad',
    name: event.event_metadata?.game_name || '',
    description: event.event_metadata?.game_mechanics_description || ''
  } : null;

  // A plain event is the member's screen, built from the design's Event Detail file. The
  // PinGolf night keeps its own layout below - cards, holes and a live leaderboard are a
  // different screen wearing the same route.
  if (!isPingolfNight && !isEditingEvent) {
    return (
      <>
        <EventDetailMember
          event={event}
          currentUser={currentUser}
          canManage={isAdmin || isHost}
          manageTo={`/events/${eventId}/manage`}
          myRsvp={myCurrentRsvp}
          onSetRsvp={(status) => saveMyRsvp({ status })}
          onSaveNote={(message) => saveMyRsvp({ message })}
          onRemoveRsvp={handleRemoveMyRsvp}
          eventUpdates={eventUpdates}
          featured={featuredItem}
          canManageFeatured={canManageFeaturedGame}
          onAssignFeatured={handleAssignGame}
          onRemoveFeatured={handleRemoveFeaturedGame}
        />

        {/* The member screen reserves room for this bar (see .edm padding-bottom) and the
            RSVP bar sits at --bn-shell-height above it, but the route never rendered one,
            so the detail page was a dead end with a gap under the RSVP choices. No tab is
            active here: this is a page you reach from the nav, not one of its destinations. */}
        <EventsBottomNav
          activeTab={null}
          onSelectTab={(key) => navigate(key === 'browse' ? '/events' : `/events?tab=${key}`)}
          isAdmin={isAdmin}
        />

        {canManageFeaturedGame && showFeaturedGameForm && featuredItem && (
          <DSModal
            title={`${featuredItem.name ? 'Edit' : 'Assign'} Featured ${featuredItemLabel}`}
            size="md"
            onClose={handleCancelFeaturedGame}
          >
            <div className="form-group">
              <DSInput
                label={`${featuredItemLabel} Name *`}
                type="text"
                value={featuredGameData.game_name}
                onChange={(e) => setFeaturedGameData({ ...featuredGameData, game_name: e.target.value })}
                placeholder={event.event_category === 'book_club' ? 'e.g., Dune, The Hobbit, Project Hail Mary' : 'e.g., Wingspan, Catan, Street Fighter 6'}
                required
              />
            </div>
            <div className="form-group">
              <DSTextarea
                label={`${featuredItemLabel} Description`}
                value={featuredGameData.game_description}
                onChange={(e) => setFeaturedGameData({ ...featuredGameData, game_description: e.target.value })}
                rows="4"
                placeholder={event.event_category === 'book_club'
                  ? 'Describe the book themes, tone, and what members can discuss (2-3 sentences)'
                  : 'Describe the game mechanics, theme, and why it is fun (2-3 sentences)'}
              />
            </div>
            <div className="form-actions">
              <DSButton appearance="outline" tone="neutral" onClick={handleCancelFeaturedGame}>
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone="primary"
                onClick={handleSaveFeaturedGame}
                disabled={!featuredGameData.game_name.trim()}
              >
                Save {featuredItemLabel}
              </DSButton>
            </div>
          </DSModal>
        )}

        {confirmDialog && (
          <DSModal title={confirmDialog.title} size="md" onClose={() => setConfirmDialog(null)}>
            <p className="pingolf-details-subtitle">{confirmDialog.message}</p>
            <div className="form-actions">
              <DSButton
                appearance="outline"
                tone="neutral"
                onClick={() => setConfirmDialog(null)}
                disabled={confirmDialogSubmitting}
              >
                Cancel
              </DSButton>
              <DSButton
                appearance="solid"
                tone={confirmDialog.danger ? 'danger' : 'primary'}
                onClick={confirmDialog.onConfirm}
                disabled={confirmDialogSubmitting}
              >
                {confirmDialogSubmitting ? 'Working...' : confirmDialog.confirmLabel}
              </DSButton>
            </div>
          </DSModal>
        )}
      </>
    );
  }

  const isPingolfEvent = event.event_metadata?.pinball_format === 'pingolf';
  // Event-level cancellation, from Series dates. Not the same as PinGolf's own canceled_at
  // below, which only stops card sales on a night that may still be happening.
  const isCancelledEvent = isEventCancelled(event);
  // A league night is one session of a week of a season: who turns up is settled by cards
  // bought on the night, not by anyone RSVPing in advance, so both RSVP blocks are noise on
  // this screen. A standalone PinGolf event still has a guest list worth managing.
  const isLeagueEvent = Boolean(event.season_week_id);
  const canManagePingolfConfig = isPingolfEvent && (isAdmin || isHost);
  const canManagePingolfPurchases = isPingolfEvent && (isAdmin || isHost);
  const pingolfFinalizedAt = event.event_metadata?.pingolf?.finalized_at;
  const pingolfCanceledAt = event.event_metadata?.pingolf?.canceled_at;
  const isPingolfFinalized = Boolean(pingolfFinalizedAt);
  const isPingolfCanceled = Boolean(pingolfCanceledAt);
  const canPurchasePingolfCards = canPurchasePingolfCardsFor(event);
  // Why purchasing is off, in the player's terms. The disabled Buy Card button carried this in
  // its label, but the button is hidden entirely once you already hold a draft card - so
  // someone who cannot buy a second card got no explanation at all. Say it in the section
  // instead, where it shows whether or not the button is on screen.
  // Finalizing a league week stamps the week, not its events, so this is the stronger fact and
  // has to be checked ahead of the event's own lifecycle flags.
  const isPingolfWeekFinalized = Boolean(event.week_finalized_at);
  // Sales being shut has three distinct player-facing meanings, and the one thing a player wants
  // from each is different: wait for this date, ask the host, or you missed it. They are not
  // separable from the clock (a host can close early or open late), so the state comes from the
  // lifecycle rule and only the wording lives here.
  const pingolfSalesClosedState = {
    not_open_yet: {
      badge: 'Sales not open yet',
      reason: `Card sales are not open yet. The host opens them when the night starts on ${formatShortDate(event.event_date)}.`
    },
    closed_by_host: {
      badge: 'Sales closed',
      reason: 'The host has closed card sales for this night, so no more cards can be bought. Ask the host if you think this is a mistake.'
    },
    night_over: {
      badge: 'Sales closed',
      reason: 'This night is over, so card sales are closed and no more cards can be bought.'
    }
  }[getPingolfSalesState(event)] || {
    badge: 'Sales closed',
    reason: 'Card sales are closed for this night, so no more cards can be bought.'
  };

  // A short badge for the header slot the Buy Card button would occupy, and the full reason for
  // the note under it. The badge alone is scannable but not self-explanatory; the sentence alone
  // is easy to skim past. Together they answer "can I buy?" and "why not?" in one glance.
  // The whole event being called off (Series dates) outranks everything PinGolf knows about
  // its own lifecycle: nobody is playing, so nobody is buying.
  const pingolfPurchaseClosedState = isCancelledEvent ? {
    badge: 'Cancelled',
    reason: 'This event is cancelled, so cards are not on sale.'
  } : canPurchasePingolfCards ? null : (
    isPingolfWeekFinalized
      ? {
        badge: 'Week finalized',
        reason: `Week ${event.week_number} is finalized. Scores are locked in, payouts have gone out, and cards are no longer on sale.`
      }
      : isPingolfFinalized
        ? {
          badge: 'Night finalized',
          reason: 'This night is finalized. Scores are locked in and no more cards can be bought.'
        }
        : isPingolfCanceled
          ? {
            badge: 'Cancelled',
            reason: 'This night was cancelled, so cards are not on sale.'
          }
          : pingolfSalesClosedState
  );
  // Stamped onto the event from its season. 'best' leagues do not have the replay gamble, so
  // the card help must not recite the most-recent-card rule at them.
  const pingolfCountsLatestCard = (event.event_metadata?.pingolf?.cardScoringMode ?? 'latest') !== 'best';
  const pingolfCardPrice = Number(event.event_metadata?.pingolf?.cardPriceDollars ?? event.event_metadata?.pingolf?.cardPrice ?? 1);
  const normalizedPingolfCardPrice = Number.isFinite(pingolfCardPrice) && pingolfCardPrice > 0 ? pingolfCardPrice : 1;
  // An abandoned card was still bought and paid for, and its money stays in the pot, so it
  // counts here too - the last fallback used to filter abandoned cards out and disagree with
  // the prize pool sitting next to it. `pingolfCards` is only a fallback in any case: it holds
  // the viewer's own cards, not the event's, so the server figure is the one to trust.
  const pingolfCardsSold = Number(event.cards_sold ?? event.event_metadata?.pingolf?.cardsSold ?? pingolfCards.length ?? 0);
  const pingolfPrizePool = Number(event.prize_pool ?? event.event_metadata?.pingolf?.prizePoolDollars ?? 0);
  // Hole columns come from the event's own lineup so the board keeps a stable, full-width
  // grid even before anyone has played every hole. Cards submitted against an older lineup
  // could carry a hole number no longer in it, so those get folded in too.
  const pingolfLeaderboardHoleNames = {};
  for (const hole of event.pingolf_holes || []) {
    pingolfLeaderboardHoleNames[hole.hole_number] = hole.machine_name;
  }
  for (const row of pingolfLeaderboard) {
    for (const hole of row.holes || []) {
      if (!pingolfLeaderboardHoleNames[hole.hole_number]) {
        pingolfLeaderboardHoleNames[hole.hole_number] = hole.machine_name || null;
      }
    }
  }
  const pingolfLeaderboardHoleNumbers = Object.keys(pingolfLeaderboardHoleNames)
    .map(Number)
    .sort((a, b) => a - b);
  // A league night's pot is pooled across the week's sessions, so name it for the week rather
  // than leaving an ambiguous "Prize Pool" that reads like this one event's take. Standalone
  // PinGolf events have no week or season, so they keep the plain label and no season tile.
  const pingolfWeekNumber = event.week_number ?? null;
  const isLeagueNight = pingolfWeekNumber !== null && pingolfWeekNumber !== undefined;
  const pingolfSeasonPool = Number(event.season_pool ?? 0);
  // Who the card is actually for decides whether a tab is on the table. For an on-behalf
  // purchase that is the selected player (the candidate roster carries tab_enabled); otherwise
  // it is the signed-in user, whose flag the hook fetches from /users/me when the modal opens.
  const pingolfPurchaseTargetUser = purchaseTargetUserId
    ? pingolfPurchaseCandidates.find(user => String(user.id) === String(purchaseTargetUserId))
    : null;
  const pingolfPurchaserCanUseTab = purchaseTargetUserId
    ? Boolean(pingolfPurchaseTargetUser?.tab_enabled)
    : Boolean(currentUserTabEnabled);
  const seasonPoolPerCard = isLeagueNight && pingolfCardsSold > 0
    ? Math.round((pingolfSeasonPool / pingolfCardsSold) * 100) / 100
    : null;
  // "Split 50/30/20" is only half the story: finalization pays each of the top three a
  // break-even floor (their buy-in back) before the percentages apply, so a $12 pot with a $5
  // floor pays 5/5/2, not 6/3.60/2.40. Preview the real numbers so nobody reads a percentage
  // off the modal and expects a different envelope. Amounts are whole dollars, matching the
  // Math.floor the server pays on.
  const pingolfWeeklyFloor = Number(event.weekly_floor_dollars ?? 5);
  const pingolfWeeklyShares = event.weekly_payout_shares || [0.5, 0.3, 0.2];
  const pingolfPayoutPreview = isLeagueNight
    ? previewWeeklyPayouts({
      pot: pingolfPrizePool,
      shares: pingolfWeeklyShares,
      floor: pingolfWeeklyFloor,
      // Places are only paid if someone finishes them, but this night's board is not the
      // week's board (the other host night pools in), so preview the full three places and
      // say so in the copy rather than shrinking the list to tonight's turnout.
      placesCount: pingolfWeeklyShares.length
    })
    : [];
  // Whether the floor rule is currently bending the percentages, which is what the copy has to
  // explain when the places do not read as 50/30/20 of the pot.
  const isPingolfPayoutFloored = pingolfPayoutPreview.some(
    (amount, index) => amount !== Math.floor(pingolfPrizePool * pingolfWeeklyShares[index])
  );
  const pingolfSplitLabel = pingolfWeeklyShares.map(share => Math.round(share * 100)).join('/');
  // The pot size at which every place's percentage finally clears the floor and the split is
  // plain percentages again.
  const pingolfStraightSplitPot = Math.ceil(pingolfWeeklyFloor / Math.min(...pingolfWeeklyShares));

  // Each tile carries its own explainer, shown in a modal on tap. The money ones especially:
  // the week pot and season pool are two halves of one card price, and nothing on the tiles
  // alone says so.
  const pingolfOverviewStats = [
    {
      key: 'card-price',
      label: 'Card Price',
      value: `$${normalizedPingolfCardPrice}`,
      icon: 'dollar',
      tone: 'brand',
      detailTitle: 'Card Price',
      detailBody: isLeagueNight
        ? `Each card costs $${normalizedPingolfCardPrice}. Part of every card goes into tonight's pot and the rest builds the season pool. You can buy more than one card - only your most recent submitted card counts for the week.`
        : `Each card costs $${normalizedPingolfCardPrice} and goes straight into this event's prize pool.`
    },
    {
      key: 'prize-pool',
      label: isLeagueNight ? `Week ${pingolfWeekNumber} Pot` : 'Prize Pool',
      value: `$${pingolfPrizePool}`,
      icon: 'trophy',
      tone: 'warning',
      detailTitle: isLeagueNight ? `Week ${pingolfWeekNumber} Pot` : 'Prize Pool',
      detailBody: isLeagueNight
        ? `Cash paid out at the end of this week to the top ${pingolfWeeklyShares.length} on the net board. It pools card sales from every host night this week, not just tonight, so both nights play for the same pot${seasonPoolPerCard ? ` - minus the $${seasonPoolPerCard} per card that goes to the season pool` : ''}.${pingolfPayoutPreview.length > 0 ? ' At the pot as it stands right now, that pays:' : ' No cards sold yet, so there is nothing in it.'}`
        : 'Cash paid out at the end of this event, split between the top finishers on the net board. It grows with every card sold.',
      // While the floor is bending the split, the percentages are not what these rows are, so
      // they'd read as a contradiction next to the amounts. The footnote carries them instead.
      detailBreakdown: pingolfPayoutPreview.map((amount, index) => ({
        key: `place-${index + 1}`,
        label: ordinalPlace(index + 1),
        amount: `$${amount}`,
        note: isPingolfPayoutFloored ? null : `${Math.round((pingolfWeeklyShares[index] || 0) * 100)}%`
      })),
      detailFootnote: isLeagueNight && pingolfPayoutPreview.length > 0
        ? [
          isPingolfPayoutFloored
            ? `Small pots pay closer to even: each paid place takes $${pingolfWeeklyFloor} off the top before the ${pingolfSplitLabel} split applies. Once the pot clears $${pingolfStraightSplitPot} it becomes a straight ${pingolfSplitLabel} split.`
            : `A straight ${pingolfSplitLabel} split, rounded down to whole dollars.`,
          'The pot grows with every card sold on either host night, so these numbers keep climbing until the week is finalized.'
        ].join(' ')
        : null
    },
    ...(isLeagueNight ? [{
      key: 'season-pool',
      label: 'Season Pool',
      value: `$${pingolfSeasonPool}`,
      icon: 'dollar',
      tone: 'success',
      detailTitle: 'Season Pool',
      detailBody: `The season-long jackpot, paid out on the final standings once every week is done. Every card sold anywhere in the season adds${seasonPoolPerCard ? ` $${seasonPoolPerCard}` : ''} to it, so it keeps climbing all season no matter which nights you play.`
    }] : []),
    {
      key: 'cards-sold',
      label: 'Cards Sold',
      value: pingolfCardsSold,
      icon: 'users',
      tone: 'info',
      detailTitle: 'Cards Sold',
      detailBody: isLeagueNight
        ? 'How many cards have been bought for tonight. More cards means a bigger pot. Cards that were started and abandoned still count - that money stays in the pot.'
        : 'How many cards have been bought for this event. More cards means a bigger prize pool.'
    }
  ];
  const playerDisplayName = (card) => (
    personName(card) || 'Player'
  );
  const getPingolfCardStatusVariant = (status) => {
    if (status === 'submitted') {
      return 'success';
    }

    if (status === 'abandoned') {
      return 'warning';
    }

    return 'info';
  };
  const myDraftPingolfCard = pingolfCards.find(card => card.status === 'draft' && card.user_id === currentUser?.id) || null;

  // Why the Buy Card button is not there. Holding a draft blocks a purchase server-side (one
  // draft per player per event), and that was the one reason the header stayed silent about -
  // the button simply vanished, which reads as a bug rather than as a rule. A closed night
  // outranks it: if sales are shut, that is the answer whether or not you hold a card.
  //
  // The one-card rule is per player, not per person tapping the button: a host or admin
  // playing their own card still has to be able to sell one to whoever is at the door. For
  // them the block moves into the purchase modal, where it applies only if the card being
  // bought is their own.
  const pingolfPurchaseBlockedState = pingolfPurchaseClosedState
    || ((myDraftPingolfCard && !canManagePingolfPurchases) ? {
      badge: 'Card in play',
      reason: `You have Card #${myDraftPingolfCard.id} in play. Finish and submit it, or abandon it, before buying another - one card at a time.`
    } : null);

  // An empty "Buy For" means the buyer themselves, which is the one case a manager's own
  // draft still rules out.
  const pingolfPurchaseIsForSelf = !purchaseTargetUserId
    || String(purchaseTargetUserId) === String(currentUser?.id);
  const pingolfSelfPurchaseBlocked = Boolean(myDraftPingolfCard) && pingolfPurchaseIsForSelf;

  // Header actions for a PinGolf night live in one Actions menu, matching the season admin
  // hero. Management-only: buying and opening cards is already handled by the My Cards
  // section below, so a plain player has nothing here and ActionsMenu renders nothing.

  // This page is the member's view of the event. Running it - the roster, check-in, hosts,
  // sending an update - is a different job with a different audience, so it is a screen of
  // its own reached from here rather than sections appended underneath.
  const canManageEvent = isAdmin || isHost;

  const pingolfEventActions = [
    canManagePingolfConfig && { label: 'Host Controls', onClick: handleJumpToPingolfHostControls },
    isAdmin && { label: 'Delete Event', onClick: handleDeleteEvent, tone: 'danger' }
  ];

  return (
    <div className="event-details-page">
      {!isEditingEvent && isPingolfEvent && (
        <div className="pingolf-hero pingolf-event-hero">
          <img src={pingolfHero} alt="" className="pingolf-hero-banner" />
          {/* Same shape as the season admin hero: back link up top, title and a single
              Actions menu sharing the bottom row. */}
          <div className="pingolf-event-hero-content">
            <DSBackLink to={pingolfBack.to}>{pingolfBack.label}</DSBackLink>
            <div className="pingolf-event-hero-title-row">
              <h1>{event.title}</h1>
              <ActionsMenu actions={pingolfEventActions} />
            </div>
          </div>
        </div>
      )}
      <EventCancelledBanner
        event={event}
        note={canManageEvent ? 'Cards and RSVPs are closed. Host tools below still work, and an admin can reinstate it from Series dates.' : null}
      />
      <div className="event-header-section">
        {!isEditingEvent ? (
          <>
            <div className="event-title-row">
              {!isPingolfEvent && <h1>{event.title}</h1>}
              {!isPingolfEvent && canManageEvent && (
                <DSButton
                  appearance="outline"
                  tone="secondary"
                  size="sm"
                  className="event-manage-link"
                  leadingIcon={<DSIcon name="wrench" size="sm" />}
                  onClick={() => navigate(`/events/${eventId}/manage`)}
                >
                  Manage
                </DSButton>
              )}
              {/* PinGolf's header actions live in the hero's Actions menu above. A regular
                  event never reaches this branch - it returns the member screen further up
                  - so the header menu that used to sit here was unreachable, and took the
                  only Copy RSVP Link button with it. Sharing now lives on the manage
                  screen, where the rest of the host's tools already are. */}
            </div>
            <div className="event-meta">
              {isPingolfEvent && (
                <>
                  <div className="pingolf-event-meta-row">
                    <p className="event-date"><span className="pingolf-inline-icon" aria-hidden="true"><DSIcon name="calendar" size="sm" tone="muted" /></span>{formatShortDate(event.event_date)}</p>
                    <p className="event-date">{formatTimeRange(event.event_date, event.duration_minutes)}</p>
                  </div>

                  <PinGolfStats
                    stats={pingolfOverviewStats}
                    className="pingolf-event-stats"
                    ariaLabel="PinGolf event stats"
                  />
                </>
              )}

              {!isPingolfEvent && (
                <>
              {event.event_category && (
                <StatusBadge variant={getCategoryVariant(event.event_category)} uppercase className="category-badge">
                  {getCategoryLabel(event.event_category)}
                </StatusBadge>
              )}
              <p className="event-date"><DSIcon name="calendar" /> {formatDate(event.event_date)}</p>
              {event.hosts && event.hosts.length > 0 && (
                <div>
                  <p className="event-hosts">
                    <DSIcon name="user" /> Host{event.hosts.length > 1 ? 's' : ''}: {event.hosts.map(h => h.preferred_name || h.first_name).join(', ')}
                  </p>
                  {event.hosts.some(h => h.host_profile) && (
                    <div style={{ marginTop: '10px' }}>
                      {event.hosts.filter(h => h.host_profile).map(host => (
                        <div key={host.id} style={{
                          marginBottom: '10px',
                          padding: '12px',
                          backgroundColor: 'var(--ds-color-surface)',
                          borderRadius: '4px',
                          borderLeft: '3px solid #007bff'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                            About {host.preferred_name || host.first_name}:
                          </div>
                          <div style={{ fontSize: '14px', fontStyle: 'italic', lineHeight: '1.5' }}>
                            {expandedHostBios[host.id] ? host.host_profile : truncateText(host.host_profile, 150)}
                            {host.host_profile.length > 150 && (
                              <button 
                                onClick={() => toggleHostBio(host.id)}
                                style={{ 
                                  marginLeft: '8px',
                                  color: 'var(--ds-color-statusInfoText)',
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0,
                                  fontSize: '14px'
                                }}
                              >
                                {expandedHostBios[host.id] ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {event.description && (
                <div className="event-description-container">
                  <p className="event-description" style={{ whiteSpace: 'pre-wrap' }}>
                    {expandedDescription
                      ? event.description
                      : truncateText(event.description, EVENT_DESCRIPTION_PREVIEW_LENGTH)}
                  </p>
                  {event.description.length > EVENT_DESCRIPTION_PREVIEW_LENGTH && (
                    <button 
                      onClick={() => setExpandedDescription(!expandedDescription)}
                      style={{ 
                        marginTop: '8px',
                        color: 'var(--ds-color-statusInfoText)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: 0,
                        fontSize: '14px'
                      }}
                    >
                      {expandedDescription ? 'Show less' : 'Read more'}
                    </button>
                  )}
                </div>
              )}
              
              {/* Pinball Tournament Details */}
              {event.event_metadata?.matchplay_url && (
                <div className="tournament-details-section" style={{
                  marginTop: '30px',
                  padding: '20px',
                  backgroundColor: 'var(--ds-color-statusInfoBg)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--ds-color-statusInfoText)'
                }}>
                  <h3 style={{ marginTop: 0, color: 'var(--ds-color-statusInfoText)' }}><DSIcon name="gamepad" /> Tournament Details</h3>
                  
                  <div className="tournament-info">
                    {event.event_metadata.matchplay_url && (
                      <div className="info-item">
                        <strong>MatchPlay Tournament:</strong>{' '}
                        <a 
                          href={event.event_metadata.matchplay_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{
                            color: 'var(--ds-color-statusInfoText)',
                            fontWeight: '600',
                            textDecoration: 'underline'
                          }}
                        >
                          Register Here <DSIcon name="arrowRight" />
                        </a>
                      </div>
                    )}
                    
                    {event.event_metadata.checkin_time && (
                      <div className="info-item">
                        <strong>Check-in:</strong> {event.event_metadata.checkin_time}
                      </div>
                    )}
                    
                    {event.event_metadata.tournament_fee && (
                      <div className="info-item">
                        <strong>Tournament Fee:</strong> ${event.event_metadata.tournament_fee}
                      </div>
                    )}
                    
                    {event.event_metadata.day_pass_fee && (
                      <div className="info-item">
                        <strong>Day Pass (non-members):</strong> ${event.event_metadata.day_pass_fee}
                      </div>
                    )}
                    
                    {event.event_metadata.num_rounds && event.event_metadata.qualifier_format && (
                      <div className="info-item">
                        <strong>Qualifiers:</strong> {event.event_metadata.num_rounds} Rounds of {event.event_metadata.qualifier_format}
                      </div>
                    )}
                    
                    {event.event_metadata.finals_format && (
                      <div className="info-item">
                        <strong>Finals Format:</strong> {event.event_metadata.finals_format}
                      </div>
                    )}
                    
                    {event.event_metadata.finals_structure && (
                      <div className="info-item">
                        <strong>Finals Structure:</strong> {event.event_metadata.finals_structure}
                      </div>
                    )}
                    
                    {event.event_metadata.prize_info && (
                      <div className="info-item">
                        <strong>Prizes:</strong> {event.event_metadata.prize_info}
                      </div>
                    )}
                  </div>
                  
                  {/* Categories */}
                  {event.categories && event.categories.length > 0 && (
                    <div className="event-categories" style={{ marginTop: '15px' }}>
                      <strong>Categories:</strong>{' '}
                      {event.categories.map((cat, idx) => (
                        <span key={cat.id}>
                          <span style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            marginLeft: '5px',
                            backgroundColor: 'var(--ds-color-statusInfoText)',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}>
                            {cat.name}
                          </span>
                          {idx < event.categories.length - 1 ? ' ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
                </>
              )}

              {isPingolfEvent && event.pingolf_holes?.length > 0 && (
                <div className="pingolf-details-section">
                  <div className="pingolf-main-grid">
                    <section className="pingolf-lineup-section">
                      <div className="pingolf-lineup-header">
                        <h2>Course Lineup</h2>
                        <span className="pingolf-lineup-count">{event.pingolf_holes.length} holes</span>
                      </div>
                      <div className="pingolf-lineup-list">
                        {event.pingolf_holes.map((hole) => (
                          <div key={hole.id} className="pingolf-lineup-item">
                            <span className="pingolf-hole-number" aria-label={`Hole ${hole.hole_number}`}>
                              {hole.hole_number}
                            </span>
                            <div className="pingolf-lineup-body">
                              <strong className="pingolf-hole-machine" title={hole.machine_name}>
                                {hole.machine_name}
                              </strong>
                              <div className="pingolf-lineup-meta">
                                <span>{formatScoreAbbreviated(hole.target_score)}</span>
                                <span className="pingolf-lineup-meta-sep" aria-hidden="true">·</span>
                                <span>{hole.max_balls} balls</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="pingolf-leaderboard-panel pingolf-leaderboard-panel-primary">
                      <div className="pingolf-leaderboard-header">
                        <h2>Leaderboard</h2>
                        <button
                          type="button"
                          className="pingolf-help-button"
                          onClick={() => setShowPingolfScoringHelp(true)}
                          aria-label="How PinGolf scoring reads"
                        >
                          <DSIcon name="info" size="sm" tone="muted" />
                        </button>
                      </div>

                      {pingolfLeaderboardError && (
                        <DSAlert variant="error" appearance="outline">{pingolfLeaderboardError}</DSAlert>
                      )}

                      {loadingPingolfLeaderboard && pingolfLeaderboard.length === 0 ? (
                        <p className="pingolf-empty-state">Loading leaderboard...</p>
                      ) : pingolfLeaderboard.length === 0 ? (
                        <p className="pingolf-empty-state">No cards sold yet.</p>
                      ) : (() => {
                        // A card still being played has no place yet (place === null). Those go
                        // in their own table below the standings, where "Thru" can be a real
                        // column instead of a tag crowding the player's name - and where a
                        // blank hole reads as "not there yet" rather than as a missing score.
                        // The +7 per unplayed hole is a settlement rule, so it is named once in
                        // that table's caption and never drawn into a cell mid-round.
                        const ranked = pingolfLeaderboard.filter(row => row.place !== null);
                        const inFlight = pingolfLeaderboard.filter(row => row.place === null);
                        const holeCount = pingolfLeaderboardHoleNumbers.length;

                        const holeHeaders = pingolfLeaderboardHoleNumbers.map(holeNumber => (
                          <th
                            key={holeNumber}
                            className="pingolf-lb-hole-col"
                            title={pingolfLeaderboardHoleNames[holeNumber] || undefined}
                          >
                            {holeNumber}
                          </th>
                        ));

                        const holeCells = (row) => {
                          const holesByNumber = new Map((row.holes || []).map(hole => [hole.hole_number, hole]));
                          return pingolfLeaderboardHoleNumbers.map(holeNumber => {
                            const hole = holesByNumber.get(holeNumber);
                            return (
                              <td
                                key={holeNumber}
                                className={`pingolf-lb-hole-col${hole?.unplayed ? ' pingolf-lb-hole-unplayed' : ''}`}
                              >
                                <PinGolfScore
                                  value={hole ? hole.hole_result : null}
                                  doubled={hole ? hole.doubled : false}
                                  title={hole?.unplayed
                                    ? `Not played - scored as +${hole.hole_result}`
                                    : (pingolfLeaderboardHoleNames[holeNumber] || undefined)}
                                />
                              </td>
                            );
                          });
                        };

                        const rowProps = (row) => ({
                          key: `${row.user_id}-${row.card_id}`,
                          onClick: () => setPingolfLeaderboardDetailRow(row),
                          role: 'button',
                          tabIndex: 0,
                          onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPingolfLeaderboardDetailRow(row);
                            }
                          }
                        });

                        return (
                          <>
                            {ranked.length > 0 && (
                              <div className="pingolf-leaderboard-table-scroll">
                                <table className="pingolf-leaderboard-table">
                                  <thead>
                                    <tr>
                                      <th className="pingolf-lb-place-col">#</th>
                                      <th className="pingolf-lb-player-col">Player</th>
                                      {holeHeaders}
                                      <th className="pingolf-lb-num-col">Card</th>
                                      <th className="pingolf-lb-num-col">Hcp</th>
                                      <th className="pingolf-lb-num-col">Net</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ranked.map(row => (
                                      <tr {...rowProps(row)}>
                                        <td className="pingolf-lb-place-col">{row.place}</td>
                                        <td className="pingolf-lb-player-col">{playerDisplayName(row)}</td>
                                        {holeCells(row)}
                                        <td className="pingolf-lb-num-col"><PinGolfScore value={row.total_score} /></td>
                                        <td className="pingolf-lb-num-col">{formatHandicap(row.playing_handicap)}</td>
                                        <td className="pingolf-lb-num-col pingolf-lb-net">
                                          <PinGolfScore value={row.net} emphasis />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {inFlight.length > 0 && (
                              <div className="pingolf-lb-in-progress-section">
                                <h3 className="pingolf-lb-in-progress-heading">In progress</h3>
                                <div className="pingolf-leaderboard-table-scroll">
                                  <table className="pingolf-leaderboard-table">
                                    <thead>
                                      <tr>
                                        <th className="pingolf-lb-player-col">Player</th>
                                        {holeHeaders}
                                        <th className="pingolf-lb-num-col">Thru</th>
                                        <th className="pingolf-lb-num-col">Card</th>
                                        <th className="pingolf-lb-num-col">Hcp</th>
                                        <th className="pingolf-lb-num-col">Net</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {inFlight.map(row => (
                                        <tr {...rowProps(row)}>
                                          <td className="pingolf-lb-player-col">{playerDisplayName(row)}</td>
                                          {holeCells(row)}
                                          <td className="pingolf-lb-num-col pingolf-lb-thru">
                                            {row.holes_played}/{holeCount}
                                          </td>
                                          <td className="pingolf-lb-num-col"><PinGolfScore value={row.total_score} /></td>
                                          <td className="pingolf-lb-num-col">{formatHandicap(row.playing_handicap)}</td>
                                          <td className="pingolf-lb-num-col pingolf-lb-net">
                                            <PinGolfScore value={row.net} emphasis />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </section>
                  </div>

                </div>
              )}

              {isPingolfEvent && (
                <section className="pingolf-cards-section">
                  {/* Title, help icon, and the buy action on one row - the same shape as the
                      leaderboard header. What used to be a standing subtitle and a status
                      sentence now lives behind the icon, which keeps the section to a single
                      line before the table starts. */}
                  <div className="pingolf-cards-header">
                    <h2>My Cards</h2>
                    <button
                      type="button"
                      className="pingolf-help-button"
                      onClick={() => setShowPingolfCardsHelp(true)}
                      aria-label="How PinGolf cards work"
                    >
                      <DSIcon name="info" size="sm" tone="muted" />
                    </button>
                    {/* No "View My Card" shortcut: clicking the draft's row opens it, and the
                        button read as a second, competing way to do the same thing.
                        The button looks the same whether or not buying is available - when it
                        is blocked it opens the reason instead of the purchase flow, so the tap
                        always does something. */}
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      size="sm"
                      onClick={() => {
                        if (pingolfPurchaseBlockedState) {
                          setShowPingolfPurchaseBlockedInfo(true);
                          return;
                        }
                        setShowPingolfPurchaseModal(true);
                        setPingolfPurchaseError(null);
                      }}
                      leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}
                    >
                      {`Buy Card ($${normalizedPingolfCardPrice})`}
                    </DSButton>
                  </div>

                  {/* No purchase error here - it is raised by, and shown inside, the purchase
                      modal. Rendering it on the page too meant the same message appeared twice
                      at once, and lingered behind the modal after it closed. */}

                  {loadingPingolfCards ? (
                    <p>Loading cards...</p>
                  ) : pingolfCards.length === 0 ? (
                    <p className="pingolf-empty-state">No cards purchased yet.</p>
                  ) : (
                    <table className="pingolf-card-table">
                      <thead>
                        <tr>
                          <th>Card</th>
                          <th className="pingolf-card-score-col">Score</th>
                          {/* Chevron column: deliberately unlabelled, it is an affordance rather
                              than a data point. Each row carries its own "Open Card #N" label. */}
                          <th className="pingolf-card-open-col" />
                        </tr>
                      </thead>
                      <tbody>
                        {pingolfCards.map(card => (
                          <tr
                            key={card.id}
                            onClick={() => navigate(`/events/${eventId}/pingolf-cards/${card.id}`)}
                            role="button"
                            tabIndex={0}
                            aria-label={`Open Card #${card.id}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/events/${eventId}/pingolf-cards/${card.id}`);
                              }
                            }}
                          >
                            <td>
                              <div className="pingolf-card-id-cell">
                                <span className="pingolf-card-id">Card #{card.id}</span>
                                <DSBadge variant={getPingolfCardStatusVariant(card.status)} appearance="outline">{card.status}</DSBadge>
                                {card.is_counting_card && <DSBadge variant="info" appearance="outline">Counting</DSBadge>}
                              </div>
                            </td>
                            <td className="pingolf-card-score-col">
                              <PinGolfScore value={card.total_score} />
                            </td>
                            {/* Hover and cursor only show up once you are already on the row, so
                                a resting chevron is what says these open at all. */}
                            <td className="pingolf-card-open-col">
                              <DSIcon name="chevronRight" size="sm" tone="muted" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              )}

              {showPingolfCardsHelp && (
                <DSModal title="About Your Cards" size="md" onClose={() => setShowPingolfCardsHelp(false)}>
                  <p className="pingolf-help-lede">
                    Every card you have bought for this night, newest first. Tap one to open its
                    scorecard and enter hole results.
                  </p>

                  <dl className="pingolf-help-columns">
                    <dt>draft</dt>
                    <dd>Still being played.</dd>
                    <dt>submitted</dt>
                    <dd>Finished and handed in.</dd>
                    <dt>abandoned</dt>
                    <dd>Closed out without finishing.</dd>
                    <dt>Counting</dt>
                    <dd>
                      {pingolfCountsLatestCard
                        ? 'The card that counts for you right now.'
                        : 'Your best card, which is the one that counts for you.'}
                    </dd>
                    <dt>Score</dt>
                    <dd>The holes you have played so far, against par.</dd>
                  </dl>

                  <div className="pingolf-help-note">
                    <h4>Buying and replaying</h4>
                    <ul>
                      <li>One card at a time. Finish and submit it, or abandon it, before buying another.</li>
                      {pingolfCountsLatestCard && (
                        <li>
                          Your <strong>most recent</strong> card is the one that counts, even if an
                          earlier one was better. Replaying is a real gamble.
                        </li>
                      )}
                      {!pingolfCountsLatestCard && (
                        <li>Your <strong>best</strong> card counts, so an extra card can only help you.</li>
                      )}
                      <li>
                        A card counts from the moment you buy it. Any hole left unplayed scores
                        {' '}<strong>+{UNPLAYED_HOLE_RESULT}</strong> once the night is settled - submitted,
                        unfinished, or abandoned alike.
                      </li>
                      <li>Every card you buy feeds the pot. There are no refunds.</li>
                    </ul>
                  </div>

                  <div className="form-actions">
                    <DSButton appearance="outline" tone="neutral" onClick={() => setShowPingolfCardsHelp(false)}>
                      Close
                    </DSButton>
                  </div>
                </DSModal>
              )}

              {showPingolfPurchaseBlockedInfo && pingolfPurchaseBlockedState && (
                <DSModal
                  title={pingolfPurchaseBlockedState.badge}
                  size="sm"
                  onClose={() => setShowPingolfPurchaseBlockedInfo(false)}
                >
                  <p className="pingolf-details-subtitle">{pingolfPurchaseBlockedState.reason}</p>
                  <div className="form-actions">
                    <DSButton
                      appearance="outline"
                      tone="neutral"
                      onClick={() => setShowPingolfPurchaseBlockedInfo(false)}
                    >
                      Got it
                    </DSButton>
                  </div>
                </DSModal>
              )}

              {showPingolfPurchaseModal && (
                <DSModal title="Confirm Card Purchase" size="md" hideClose onClose={() => setShowPingolfPurchaseModal(false)}>
                  <p className="pingolf-details-subtitle">Buy a PinGolf card for ${normalizedPingolfCardPrice.toFixed(2)}?</p>

                  {pingolfPurchaseError && (
                    <DSAlert variant="error" appearance="outline">{pingolfPurchaseError}</DSAlert>
                  )}

                  {pingolfSelfPurchaseBlocked && (
                    <DSAlert variant="info" appearance="outline">
                      You have Card #{myDraftPingolfCard.id} in play, so you cannot buy a second one
                      for yourself. Pick someone in Buy For to sell them a card.
                    </DSAlert>
                  )}

                  {/* A host frequently buys for someone standing at the door with cash and no
                      credits, and the roster runs to hundreds of people, so this is a searchable
                      combobox. Cleared means "myself". */}
                  {canManagePingolfPurchases && (
                    <div className="pingolf-buy-for">
                      <label className="pingolf-buy-for-label" htmlFor="pingolf-buy-for-user">Buy For</label>
                      <Select
                        inputId="pingolf-buy-for-user"
                        className="pingolf-buy-for-picker"
                        classNamePrefix="pingolf-buy-for-select"
                        options={pingolfPurchaseUserOptions}
                        value={selectedPingolfPurchaseUserOption}
                        onChange={(option) => setPurchaseTargetUserId(option ? String(option.value) : '')}
                        isClearable
                        isSearchable
                        placeholder="Myself - or search by name, username, or email"
                        menuPortalTarget={document.body}
                        noOptionsMessage={() => 'No matching people'}
                        filterOption={(candidate, input) => {
                          if (!input) {
                            return true;
                          }
                          return candidate.data.searchText.includes(input.toLowerCase());
                        }}
                      />
                    </div>
                  )}

                  {/* Only meaningful for someone who actually has tab access - the server
                      ignores useTab otherwise, so offering it to everyone advertised an option
                      that silently did nothing and made "Insufficient credits" look like a bug.
                      On an on-behalf purchase the buyer's own access is irrelevant; what matters
                      is the player being bought for. */}
                  {pingolfPurchaserCanUseTab && (
                    <label className="pingolf-tab-toggle">
                      <input
                        type="checkbox"
                        checked={useTabForPingolfPurchase}
                        onChange={(e) => setUseTabForPingolfPurchase(e.target.checked)}
                      />
                      <span>Allow tab if credits are insufficient</span>
                    </label>
                  )}

                  {/* Own class, not .form-actions - that one is defined unscoped in
                      PinballEventForm.css and EventsHub.css, which stack it into a column on
                      mobile and add a 2px rule above it. */}
                  <div className="pingolf-modal-actions">
                    <DSButton
                      appearance="outline"
                      tone="neutral"
                      onClick={() => {
                        setShowPingolfPurchaseModal(false);
                        setPurchaseTargetUserId('');
                      }}
                      disabled={buyingPingolfCard}
                    >
                      Cancel
                    </DSButton>
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      onClick={handlePurchasePingolfCard}
                      disabled={buyingPingolfCard || !canPurchasePingolfCards || pingolfSelfPurchaseBlocked}
                    >
                      {buyingPingolfCard ? 'Processing...' : 'Confirm Purchase'}
                    </DSButton>
                  </div>
                </DSModal>
              )}

              {showPingolfScoringHelp && (
                <DSModal title="Reading the Leaderboard" size="md" onClose={() => setShowPingolfScoringHelp(false)}>
                  <PinGolfScoreLegend />

                  <p className="pingolf-help-lede">
                    Lower is better, so a score under par is good.
                  </p>

                  {/* A glossary reads far better as a column of terms than as a sentence listing
                      them, since people open this to look one thing up, not to read it through. */}
                  <dl className="pingolf-help-columns">
                    <dt>1-9</dt>
                    <dd>That hole's result against par.</dd>
                    <dt>Card</dt>
                    <dd>The whole card added up.</dd>
                    <dt>Hcp</dt>
                    <dd>Playing handicap, averaged from the player's past cards.</dd>
                    <dt>Net</dt>
                    <dd>Card plus handicap. <strong>Net ranks the board.</strong></dd>
                  </dl>

                  {/* The in-progress table shows what has been shot so far and nothing more, so
                      the rule that gives those blanks a value at the end is explained here
                      rather than crowding the board with it while people are still playing. */}
                  <div className="pingolf-help-note">
                    <h4>In progress</h4>
                    <ul>
                      <li>Cards still being played. <strong>Thru</strong> is how many holes are done.</li>
                      <li>Their scores cover only those holes, so they are not ranked against finished cards yet.</li>
                      <li>
                        Once the night is settled, every unplayed hole scores
                        {' '}<strong>+{UNPLAYED_HOLE_RESULT}</strong> and the card takes its place on the board.
                      </li>
                      <li>A card counts from the moment it is bought - submitted, unfinished, or abandoned.</li>
                    </ul>
                  </div>
                  <div className="form-actions">
                    <DSButton appearance="outline" tone="neutral" onClick={() => setShowPingolfScoringHelp(false)}>
                      Close
                    </DSButton>
                  </div>
                </DSModal>
              )}

              {pingolfLeaderboardDetailRow && (
                <DSModal title="Leaderboard Details" size="md" onClose={() => setPingolfLeaderboardDetailRow(null)}>
                    <p><strong>{playerDisplayName(pingolfLeaderboardDetailRow)}</strong></p>
                    <p>Card (vs par): <PinGolfScore value={pingolfLeaderboardDetailRow.total_score} emphasis /></p>
                    <p>Playing handicap: <strong>{formatHandicap(pingolfLeaderboardDetailRow.playing_handicap)}</strong></p>
                    <p>Net: <PinGolfScore value={pingolfLeaderboardDetailRow.net} emphasis /></p>
                    <p className="pingolf-details-subtitle">
                      Net = card plus the strokes your handicap applies:{' '}
                      {describeNet(pingolfLeaderboardDetailRow.total_score, pingolfLeaderboardDetailRow.playing_handicap)}
                      {' = '}{formatVsPar(pingolfLeaderboardDetailRow.net)}. Your playing handicap is your
                      running average from past cards, shown as the strokes it applies here. Lower net is better.
                    </p>
                    <div className="form-actions">
                      <DSButton
                        appearance="outline"
                        tone="neutral"
                        onClick={() => setPingolfLeaderboardDetailRow(null)}
                      >
                        Close
                      </DSButton>
                    </div>
                </DSModal>
              )}

              {/* Abandon and Reopen moved to PinGolfCard: the card list here only opens cards. */}

            </div>
          </>
        ) : (
          <div className="edit-event-overlay" onClick={handleCancelEditEvent}>
            <div className="edit-event-form" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Event</h2>
              <div className="form-group">
                <DSInput
                  label="Title *"
                  type="text"
                  value={editEventData.title}
                  onChange={(e) => setEditEventData({...editEventData, title: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <DSTextarea
                  label="Description"
                  value={editEventData.description}
                  onChange={(e) => setEditEventData({...editEventData, description: e.target.value})}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Photo</label>
                <EventPhotoControl event={event} onChange={handleEventPhotoChange} />
              </div>
              <div className="form-group">
                <DSSelect
                  label="Event Category *"
                  value={editEventData.event_category}
                  onChange={(e) => setEditEventData({...editEventData, event_category: e.target.value})}
                  required
                >
                  <option value="pinball">Pinball</option>
                  <option value="video_game">Video Game</option>
                  <option value="board_game">Board Game</option>
                  <option value="book_club">Book Club</option>
                  <option value="other">Other</option>
                </DSSelect>
              </div>

              <div className="form-group">
                <DSInput
                  label="Date & Time *"
                  type="datetime-local"
                  value={editEventData.event_date}
                  onChange={(e) => setEditEventData({...editEventData, event_date: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <DSInput
                  label="End time"
                  type="time"
                  value={editEventData.end_time || ''}
                  onChange={(e) => setEditEventData({ ...editEventData, end_time: e.target.value })}
                />
                <small>An end before the start time runs past midnight.</small>
              </div>
              {/* Decided from the saved event, so clearing a field mid-edit doesn't hide the section. */}
              {isTournament(event) && (
                <>
                  <h3>Tournament details</h3>
                  <TournamentDetailsFields metadata={editEventData.event_metadata} onChange={handleTournamentFieldChange} />
                </>
              )}
              {isAdmin && (
                <div className="form-group">
                  <DSInput
                    label="Website event"
                    type="text"
                    placeholder="https://capitalpinballclub.com/event/your-event/"
                    value={editEventData.wordpress_link || ''}
                    onChange={(e) => setEditEventData({ ...editEventData, wordpress_link: e.target.value })}
                  />
                  <small>
                    The website page this event syncs to. Paste an existing page's address to update
                    it instead of creating a new one. Clear it to unlink.
                  </small>
                </div>
              )}
              {isAdmin && (
                <div className="form-group">
                  <DSCheckbox
                    id="edit-event-private"
                    label="Private event"
                    checked={Boolean(editEventData.is_private)}
                    onChange={(e) => setEditEventData({ ...editEventData, is_private: e.target.checked })}
                  />
                  <small>
                    Kept off the website and the events lists. People with the link can still open it
                    and RSVP. The hallway sign shows only the hosts and the time.
                  </small>
                </div>
              )}
              {editEventData.is_private && (
                <div className="form-group">
                  <DSInput
                    label="Hallway sign label"
                    type="text"
                    placeholder="Leave empty to show only the time"
                    value={hallwayLabel}
                    onChange={(e) => setHallwayLabel(e.target.value)}
                  />
                  <small>Shown under the hosts on the hallway sign instead of the event title.</small>
                </div>
              )}
              {/* A banner on the hallway sign (/hallway/tv) for the day of this event. */}
              <div className="form-group">
                <DSSelect
                  label="Hallway sign notice"
                  value={hallwayNotice?.kind || ''}
                  onChange={(e) => setHallwayNotice(
                    e.target.value === 'quiet_room'
                      ? { kind: 'quiet_room', room: hallwayNotice?.room || '' }
                      : e.target.value ? { kind: e.target.value } : null
                  )}
                >
                  <option value="">None</option>
                  <option value="quiet_room">Quiet room (no pinball in a room)</option>
                  <option value="maintenance">Maintenance day</option>
                </DSSelect>
                <small>
                  <a href={`/hallway/tv?event=${eventId}`} target="_blank" rel="noopener noreferrer">
                    Preview on the hallway sign
                  </a>
                  {' '}(shows the saved event, as it will look when it starts)
                </small>
              </div>
              {hallwayNotice?.kind === 'quiet_room' && (
                <div className="form-group">
                  <DSInput
                    label="Quiet room name"
                    type="text"
                    placeholder="Yellow Room"
                    value={hallwayNotice.room}
                    onChange={(e) => setHallwayNotice({ kind: 'quiet_room', room: e.target.value })}
                  />
                </div>
              )}
              <div className="form-actions">
                <DSButton appearance="outline" tone="neutral" onClick={handleCancelEditEvent}>
                  Cancel
                </DSButton>
                <DSButton appearance="solid" tone="primary" onClick={handleSaveEvent}>
                  Save Changes
                </DSButton>
              </div>
            </div>
          </div>
        )}

        {isGameEvent && (isAdmin || isHost) && (
          <div className="event-payment-section">
            <h3><DSIcon name="card" /> Guest Payment Links</h3>
            <p className="event-payment-description">
              Share a direct link or display a QR code so guests can pay quickly.
            </p>

            <div className="event-payment-cards">
              {EVENT_PAYMENT_LINKS.map((paymentLink) => (
                <DSCard key={paymentLink.id} variant="elevated" bgVariant="default" borderVariant="subtle" cornerVariant="lg" className="event-payment-card">
                  <h4>{paymentLink.title}</h4>
                  <div className="event-payment-actions">
                    <DSButton
                      appearance="outline"
                      tone="secondary"
                      onClick={() => copyPaymentLink(paymentLink)}
                    >
                      <DSIcon name="clipboard" /> Copy Link
                    </DSButton>
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      onClick={() => showPaymentQr(paymentLink)}
                    >
                      <DSIcon name="expand" /> QR Code
                    </DSButton>
                  </div>
                </DSCard>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {activePaymentQr && (
        <div className="payment-qr-overlay" onClick={closePaymentQr}>
          <div className="payment-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-qr-header">
              <h3>{activePaymentQr.title}</h3>
              <DSButton appearance="outline" tone="neutral" size="sm" onClick={closePaymentQr} leadingIcon={<DSIcon name="x" />}>Close
              </DSButton>
            </div>
            <div className="payment-qr-code">
              <QRCode
                value={activePaymentQr.url}
                size={Math.min(window.innerWidth - 80, 420)}
                level="H"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Event Updates Section - visible to everyone */}
      {eventUpdates.length > 0 && (
        <div className="event-updates-section">
          <h2><DSIcon name="broadcast" /> Event Updates</h2>
          <div className="updates-list">
            {eventUpdates.map((update) => (
              <div key={update.id} className="update-item">
                <div className="update-header">
                  <span className="update-sender">
                    {update.sender_name || 'Event Host'}
                  </span>
                  <span className="update-date">
                    {new Date(update.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="update-message" style={{ whiteSpace: 'pre-wrap' }}>
                  {update.message}
                </p>
                <div className="update-footer">
                  <span className="update-recipients">
                    Sent to {update.recipient_count} attendee{update.recipient_count !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* A cap is a fact a member needs before deciding. A headcount is not: it tells
          everyone how popular the night is and how many friends someone brought, which is
          the host's business rather than the room's. */}
      {event.max_capacity ? (
        <div className="event-capacity-note">
          <DSIcon name="user" size="sm" tone="muted" />
          Limited to {event.max_capacity} players for this night
        </div>
      ) : null}

      {/* A cancelled event takes no RSVPs (the server refuses them too). Someone already on
          the list can still take their name off. */}
      {currentUser && !isLeagueEvent && isCancelledEvent && myCurrentRsvp && (
        <div className="user-rsvp-section">
          <p>RSVPs are closed because this event is cancelled.</p>
          <DSButton appearance="text" tone="danger" onClick={handleRemoveMyRsvp}>
            Remove my RSVP
          </DSButton>
        </div>
      )}

      {/* Show RSVP section to any logged-in user, including admins/hosts, so they can indicate their own attendance */}
      {currentUser && !isLeagueEvent && !isCancelledEvent && (
        <div className="user-rsvp-section">
          {myCurrentRsvp ? (
            <div className="attendee-message">
              <h2>You're registered for this event!</h2>
              <p>Current Status: <strong>{myCurrentRsvp.status === 'going' ? 'Going' : myCurrentRsvp.status === 'interested' ? 'Interested' : 'Not Going'}</strong></p>
              {myCurrentRsvp.message && <p>Your message: "{myCurrentRsvp.message}"</p>}
              <p>Check your email for your QR code and event details.</p>
              
              {!showMyRsvpForm ? (
                <div className="my-rsvp-actions">
                  <DSButton
                    appearance="solid"
                    tone="primary"
                    onClick={() => setShowMyRsvpForm(true)}
                  >
                    <DSIcon name="edit" /> Update My RSVP
                  </DSButton>
                  {/* Setting the status to "Not going" leaves you on the list saying no.
                      Taking your name off entirely was only possible by asking a host. */}
                  <DSButton
                    appearance="text"
                    tone="danger"
                    onClick={handleRemoveMyRsvp}
                  >
                    Remove my RSVP
                  </DSButton>
                </div>
              ) : (
                <div className="my-rsvp-form">
                  <h3>Update My RSVP</h3>
                  <div className="form-group">
                    <DSSelect
                      label="Status:"
                      value={myRsvpData.status}
                      onChange={(e) => setMyRsvpData({...myRsvpData, status: e.target.value})}
                      required
                    >
                      <option value="going">Going</option>
                      <option value="interested">Interested</option>
                      <option value="not_going">Not Going</option>
                    </DSSelect>
                  </div>
                  <div className="form-group">
                    <DSTextarea
                      label="Message (Optional):"
                      value={myRsvpData.message}
                      onChange={(e) => setMyRsvpData({...myRsvpData, message: e.target.value})}
                      placeholder="Add a message..."
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <DSButton
                      appearance="outline"
                      tone="neutral"
                      onClick={() => {
                        setShowMyRsvpForm(false);
                        setMyRsvpData({ status: myCurrentRsvp.status, message: myCurrentRsvp.message || '' });
                      }}
                    >
                      Cancel
                    </DSButton>
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      onClick={handleMyRsvp}
                    >
                      Update RSVP
                    </DSButton>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-rsvp-message">
              <h2>RSVP for This Event</h2>
              <p>You haven't registered for this event yet.</p>
              
              {!showMyRsvpForm ? (
                <DSButton
                  appearance="solid"
                  tone="primary"
                  onClick={() => setShowMyRsvpForm(true)}
                  // leadingIcon, not a child: only the slot gets the rule that makes an
                  // unopinionated icon inherit the button's text colour, so a child icon
                  // stays near-white and shows up as a white check on gold.
                  leadingIcon={<DSIcon name="check" size="sm" />}
                >
                  RSVP Now
                </DSButton>
              ) : (
                <div className="my-rsvp-form">
                  <h3>RSVP for This Event</h3>
                  <div className="form-group">
                    <DSSelect
                      label="Status:"
                      value={myRsvpData.status}
                      onChange={(e) => setMyRsvpData({...myRsvpData, status: e.target.value})}
                      required
                    >
                      <option value="going">Going</option>
                      <option value="interested">Interested</option>
                      <option value="not_going">Not Going</option>
                    </DSSelect>
                  </div>
                  <div className="form-group">
                    <DSTextarea
                      label="Message (Optional):"
                      value={myRsvpData.message}
                      onChange={(e) => setMyRsvpData({...myRsvpData, message: e.target.value})}
                      placeholder="Add a message..."
                      rows="3"
                    />
                  </div>
                  <div className="form-actions">
                    <DSButton
                      appearance="outline"
                      tone="neutral"
                      onClick={() => {
                        setShowMyRsvpForm(false);
                        setMyRsvpData({ status: 'going', message: '' });
                      }}
                    >
                      Cancel
                    </DSButton>
                    <DSButton
                      appearance="solid"
                      tone="primary"
                      onClick={handleMyRsvp}
                    >
                      Submit RSVP
                    </DSButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}


      {confirmDialog && (
        <DSModal title={confirmDialog.title} size="md" onClose={() => setConfirmDialog(null)}>
          <p className="pingolf-details-subtitle">{confirmDialog.message}</p>
          <div className="form-actions">
            <DSButton
              appearance="outline"
              tone="neutral"
              onClick={() => setConfirmDialog(null)}
              disabled={confirmDialogSubmitting}
            >
              Cancel
            </DSButton>
            <DSButton
              appearance="solid"
              tone={confirmDialog.danger ? 'danger' : 'primary'}
              onClick={confirmDialog.onConfirm}
              disabled={confirmDialogSubmitting}
            >
              {confirmDialogSubmitting ? 'Working...' : confirmDialog.confirmLabel}
            </DSButton>
          </div>
        </DSModal>
      )}
    </div>
  );
}

export default EventDetails;
